/* eslint react-hooks/exhaustive-deps: "error" */
import React from 'react';
import { useLazyQuery } from '@apollo/client';
import { useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { PrimaryButton, Loader } from '@crehana/ui';

import { useCrehanaLiveTranslations } from 'Jsx/CrehanaLive/hooks';

import { CountryPrefix } from 'Jsx/global.d';

import { MainMenuWrapperProps } from 'Jsx/CrehanaWeb/MainMenuWrapper';

import matchMediaQuery from 'Jsx/Utils/matchMediaQuery';

import GET_LIVE_DETAIL from '../../graphql/GetLiveDetail.graphql';
import {
  GetLiveDetail,
  GetLiveDetailVariables,
} from '../../graphql/types/GetLiveDetail';
import { LiveNode } from '../../graphql/types/LiveNode';

import Detail from './Detail';

const DetailWrapper = styled.div<{ isMobileApp?: boolean }>`
  height: ${props => (props.isMobileApp ? '100vh' : 'calc(100vh - 52px)')};
  ${props => props.theme.breakpoints.up('md')} {
    height: ${props => (props.isMobileApp ? '100vh' : 'calc(100vh - 72px)')};
  }
`;

type Props = {
  setMainMenuProps: (
    props: Partial<MainMenuWrapperProps['layoutConfig']>,
  ) => void;
  enrollToLive: (liveId: number) => Promise<void>;
  isMobileApp?: boolean;
  countryPrefix: CountryPrefix;
};

const RootDetail: React.FC<Props> = ({
  setMainMenuProps,
  enrollToLive,
  isMobileApp,
  countryPrefix,
}) => {
  const { t } = useCrehanaLiveTranslations();
  const reactRouterParams = useParams<{ liveSlug: string }>();
  const { state } = useLocation<{ liveDetail: LiveNode }>();
  const [getLiveDetail, response] = useLazyQuery<
    GetLiveDetail,
    GetLiveDetailVariables
  >(GET_LIVE_DETAIL, { variables: { slug: reactRouterParams.liveSlug } });

  const [liveQuery, setLiveQuery] = React.useState<LiveNode>();

  React.useEffect(() => {
    const isMobile = matchMediaQuery('md');

    if (isMobile) {
      setMainMenuProps({
        hideSearch: true,
        hideInboxLink: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (state && state.liveDetail) {
      setLiveQuery(state.liveDetail);
    } else {
      getLiveDetail();
    }
  }, [getLiveDetail, state]);

  React.useEffect(() => {
    if (response.data?.live) {
      setLiveQuery(response.data.live);
    }
  }, [response.data]);

  if (response.error) {
    return (
      <div className="pt-64 flex flex-col items-center">
        <span className="font-body3 text-white">{t('ERROR_LOAD_DETAIL')}</span>
        <br />
        <PrimaryButton
          className="mt-32"
          label={t('RETRY_AGAIN_LABEL')}
          onClick={() => window.location.reload()}
          darkMode
        />
      </div>
    );
  }

  if (response.loading || !liveQuery) {
    return (
      <div className="pt-64" style={{ minHeight: 448 }}>
        <div className="mt-32 flex justify-center">
          <Loader white />
        </div>
      </div>
    );
  }

  return (
    <DetailWrapper isMobileApp={isMobileApp}>
      <Detail
        live={liveQuery}
        enrollToLive={enrollToLive}
        countryPrefix={countryPrefix}
      />
    </DetailWrapper>
  );
};

export default RootDetail;
